import { z } from 'zod'
import type { CreateUserPayload } from '@/types/user.types'

const workLocationEnum = z.enum(['remote', 'onsite', 'hybrid'])
const compensationTypeEnum = z.enum(['salaried', 'hourly'])

const baseUserFields = {
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roleId: z.string().min(1, 'Role is required'),
  // Optional in practice: an empty value is allowed and omitted from the
  // payload (the backend treats a blank string as invalid, not absent).
  joiningDate: z.string(),
  workLocation: workLocationEnum,
} as const

const requiredAmount = (label: string) =>
  z
    .union([z.number().min(0, `${label} must be ≥ 0`), z.nan()])
    .refine((v) => typeof v === 'number' && !Number.isNaN(v), {
      message: `${label} is required`,
    })

/**
 * Discriminated union mirroring the backend XOR validator. A salaried user
 * must provide baseSalary; an hourly user must provide hourlyRate. The
 * conditional field rendering in CreateUserForm keys off compensationType.
 */
export const createUserSchema = z.discriminatedUnion('compensationType', [
  z.object({
    ...baseUserFields,
    compensationType: z.literal('salaried'),
    baseSalary: requiredAmount('Base salary'),
  }),
  z.object({
    ...baseUserFields,
    compensationType: z.literal('hourly'),
    hourlyRate: requiredAmount('Hourly rate'),
  }),
])

export type CreateUserFormValues = z.infer<typeof createUserSchema>
export { compensationTypeEnum }

export function formValuesToCreateUserPayload(
  values: CreateUserFormValues,
  currency: string,
): CreateUserPayload {
  const compensation =
    values.compensationType === 'hourly'
      ? {
          compensationType: 'hourly' as const,
          hourlyRate: values.hourlyRate as number,
          currency,
        }
      : {
          compensationType: 'salaried' as const,
          baseSalary: values.baseSalary as number,
          currency,
        }
  // joiningDate is optional. The backend's @IsOptional() only skips an
  // absent/undefined value — an empty string still hits @IsDateString() and
  // fails ("must be a valid ISO 8601 date string"), so omit the key when blank.
  const joiningDate = values.joiningDate?.trim()
  const payload: CreateUserPayload = {
    email: values.email.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    roleId: values.roleId,
    employment: {
      ...(joiningDate ? { joiningDate } : {}),
      workLocation: values.workLocation,
    },
    compensation,
  }
  return payload
}
